import { useCallback, useEffect, useMemo, useState } from "react";
import HomeLayout from "../layouts/HomeLayout";
import { deepMergeSiteDefaults } from "../utils/siteDefaults";

async function parseJson(response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload?.ok) {
    const message = payload?.error || `Request failed (${response.status})`;
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  return payload;
}

export default function AdminPage() {
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginKey, setLoginKey] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginMsg, setLoginMsg] = useState("");

  const [data, setData] = useState(null);
  const [siteBusy, setSiteBusy] = useState(false);
  const [siteMsg, setSiteMsg] = useState("");
  const [latestBackup, setLatestBackup] = useState(null);

  const [menuFiles, setMenuFiles] = useState([]);
  const [menuCurrent, setMenuCurrent] = useState("");
  const [menuEnabled, setMenuEnabled] = useState(true);
  const [uploadDate, setUploadDate] = useState(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  });
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");

  const [galleryEnabled, setGalleryEnabled] = useState(true);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryBusy, setGalleryBusy] = useState(false);
  const [galleryMsg, setGalleryMsg] = useState("");

  const handleUnauthorized = useCallback((error) => {
    if (error?.status === 401) {
      setIsAuthenticated(false);
      setData(null);
      setSiteMsg("");
      setLoginMsg("Relace vypršela. Přihlas se znovu.");
      return true;
    }
    return false;
  }, []);

  const authFetch = useCallback(
    async (url, options = {}) => {
      const response = await fetch(url, {
        credentials: "same-origin",
        ...options,
      });
      try {
        return await parseJson(response);
      } catch (error) {
        if (handleUnauthorized(error)) {
          throw error;
        }
        throw error;
      }
    },
    [handleUnauthorized]
  );

  const loadAuthStatus = useCallback(async () => {
    const response = await fetch("/api/auth.php?action=status", {
      cache: "no-store",
      credentials: "same-origin",
    });
    const payload = await response.json().catch(() => ({}));
    setIsAuthenticated(Boolean(payload?.authenticated));
    setIsCheckingAuth(false);
  }, []);

  const loadGallery = useCallback(async () => {
    const j = await authFetch("/api/gallery.php?action=list", { cache: "no-store" });
    setGalleryEnabled(Boolean(j.enabled));
    setGalleryImages(Array.isArray(j.images) ? j.images : []);
    return j;
  }, [authFetch]);

  const uploadImage = useCallback(
    async (file, label = "") => {
      if (!file) return;
      setGalleryMsg("");
      setGalleryBusy(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("label", label);
        const j = await authFetch("/api/gallery.php?action=upload", {
          method: "POST",
          body: fd,
        });
        setGalleryMsg(`Nahráno: ${j.uploaded}`);
        await loadGallery();
      } catch (error) {
        if (!handleUnauthorized(error)) {
          setGalleryMsg(`Chyba: ${error?.message || String(error)}`);
        }
      } finally {
        setGalleryBusy(false);
      }
    },
    [authFetch, handleUnauthorized, loadGallery]
  );

  const deleteImage = useCallback(
    async (name) => {
      if (!name || !confirm(`Smazat fotku "${name}"?`)) return;
      setGalleryMsg("");
      setGalleryBusy(true);
      try {
        await authFetch("/api/gallery.php?action=delete", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name }),
        });
        await loadGallery();
      } catch (error) {
        if (!handleUnauthorized(error)) {
          setGalleryMsg(`Chyba: ${error?.message || String(error)}`);
        }
      } finally {
        setGalleryBusy(false);
      }
    },
    [authFetch, handleUnauthorized, loadGallery]
  );

  const setGalleryEnabledApi = useCallback(
    async (enabled) => {
      setGalleryMsg("");
      try {
        await authFetch("/api/gallery.php?action=setEnabled", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ enabled }),
        });
        await loadGallery();
      } catch (error) {
        if (!handleUnauthorized(error)) {
          setGalleryMsg(`Chyba: ${error?.message || String(error)}`);
        }
      }
    },
    [authFetch, handleUnauthorized, loadGallery]
  );

  const loadMenu = useCallback(async () => {
    const j = await authFetch("/api/menu.php?action=list", { cache: "no-store" });
    setMenuFiles(Array.isArray(j.files) ? j.files : []);
    setMenuCurrent(typeof j.current === "string" ? j.current : "");
    setMenuEnabled(Boolean(j.enabled));
    return j;
  }, [authFetch]);

  const uploadPdf = useCallback(
    async (file) => {
      if (!file) return;
      setUploadMsg("");
      setUploadBusy(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("date", uploadDate);
        const j = await authFetch("/api/menu.php?action=upload", {
          method: "POST",
          body: fd,
        });
        setUploadMsg(`Nahráno: ${j.uploaded}`);
        await authFetch("/api/menu.php?action=setCurrent", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: j.uploaded }),
        });
        await loadMenu();
      } catch (error) {
        if (!handleUnauthorized(error)) {
          setUploadMsg(`Chyba: ${error?.message || String(error)}`);
        }
      } finally {
        setUploadBusy(false);
      }
    },
    [authFetch, handleUnauthorized, loadMenu, uploadDate]
  );

  const setCurrentPdf = useCallback(
    async (name) => {
      setUploadMsg("");
      try {
        await authFetch("/api/menu.php?action=setCurrent", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name }),
        });
        await loadMenu();
      } catch (error) {
        if (!handleUnauthorized(error)) {
          setUploadMsg(`Chyba: ${error?.message || String(error)}`);
        }
      }
    },
    [authFetch, handleUnauthorized, loadMenu]
  );

  const setStableEnabled = useCallback(
    async (enabled) => {
      setUploadMsg("");
      try {
        await authFetch("/api/menu.php?action=setEnabled", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ enabled }),
        });
        await loadMenu();
      } catch (error) {
        if (!handleUnauthorized(error)) {
          setUploadMsg(`Chyba: ${error?.message || String(error)}`);
        }
      }
    },
    [authFetch, handleUnauthorized, loadMenu]
  );

  const deletePdf = useCallback(async () => {
    if (!menuCurrent || !confirm(`Smazat PDF "${menuCurrent}"?`)) return;
    setUploadMsg("");
    try {
      await authFetch("/api/menu.php?action=delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: menuCurrent }),
      });
      await loadMenu();
    } catch (error) {
      if (!handleUnauthorized(error)) {
        setUploadMsg(`Chyba: ${error?.message || String(error)}`);
      }
    }
  }, [authFetch, handleUnauthorized, loadMenu, menuCurrent]);

  const loadSiteStatus = useCallback(async () => {
    const j = await authFetch("/api/site.php?action=status", { cache: "no-store" });
    setLatestBackup(j.latestBackup || null);
    return j;
  }, [authFetch]);

  const loadSiteData = useCallback(async () => {
    const response = await fetch("/content/site.json", {
      cache: "no-store",
      credentials: "same-origin",
    });
    if (!response.ok) {
      throw new Error("Nepodařilo se načíst site.json");
    }
    const json = await response.json();
    setData(deepMergeSiteDefaults(json));
  }, []);

  const loadAdminData = useCallback(async () => {
    await Promise.all([loadSiteData(), loadMenu(), loadGallery(), loadSiteStatus()]);
  }, [loadGallery, loadMenu, loadSiteData, loadSiteStatus]);

  useEffect(() => {
    loadAuthStatus().catch(() => {
      setIsAuthenticated(false);
      setIsCheckingAuth(false);
    });
  }, [loadAuthStatus]);

  useEffect(() => {
    if (!isAuthenticated) return;
    loadAdminData().catch((error) => {
      handleUnauthorized(error);
    });
  }, [handleUnauthorized, isAuthenticated, loadAdminData]);

  const submitLogin = useCallback(
    async (event) => {
      event.preventDefault();
      setLoginBusy(true);
      setLoginMsg("");
      try {
        const j = await authFetch("/api/auth.php?action=login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ adminKey: loginKey }),
        });
        if (!j.authenticated) {
          throw new Error("Login se nezdařil");
        }
        setIsAuthenticated(true);
        setLoginKey("");
        await loadAdminData();
      } catch (error) {
        setLoginMsg(error?.message || "Přihlášení selhalo");
      } finally {
        setLoginBusy(false);
      }
    },
    [authFetch, loadAdminData, loginKey]
  );

  const saveSiteJson = useCallback(async () => {
    if (!data) return;
    setSiteBusy(true);
    setSiteMsg("");
    try {
      const sitePayload = {
        ...data,
        stableMenu: {
          ...(data.stableMenu || {}),
          enabled: menuEnabled,
          currentPdf: menuCurrent,
        },
      };
      const j = await authFetch("/api/site.php?action=save", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ site: sitePayload }),
      });
      setLatestBackup(j.latestBackup || null);
      setData(deepMergeSiteDefaults(sitePayload));
      setSiteMsg("Uloženo na server");
    } catch (error) {
      if (!handleUnauthorized(error)) {
        setSiteMsg(`Chyba: ${error?.message || String(error)}`);
      }
    } finally {
      setSiteBusy(false);
    }
  }, [authFetch, data, handleUnauthorized, menuCurrent, menuEnabled]);

  const restoreLatestBackup = useCallback(async () => {
    setSiteBusy(true);
    setSiteMsg("");
    try {
      const j = await authFetch("/api/site.php?action=restoreLatest", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      setData(deepMergeSiteDefaults(j.site || null));
      setSiteMsg(`Obnoveno ze zálohy: ${j.restored}`);
      await loadMenu();
      await loadGallery();
      await loadSiteStatus();
    } catch (error) {
      if (!handleUnauthorized(error)) {
        setSiteMsg(`Chyba: ${error?.message || String(error)}`);
      }
    } finally {
      setSiteBusy(false);
    }
  }, [authFetch, handleUnauthorized, loadGallery, loadMenu, loadSiteStatus]);

  const logout = useCallback(async () => {
    try {
      await authFetch("/api/auth.php?action=logout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
    } catch {
      // ignore logout transport errors and still leave admin view
    } finally {
      setIsAuthenticated(false);
      setData(null);
      window.location.replace("/");
    }
  }, [authFetch]);

  const stableMenu = useMemo(
    () => ({ enabled: menuEnabled, currentPdf: menuCurrent, files: menuFiles }),
    [menuEnabled, menuCurrent, menuFiles]
  );

  if (isCheckingAuth) {
    return <div style={{ padding: 20 }}>Ověřuji přihlášení…</div>;
  }

  if (!isAuthenticated) {
    return (
      <div className="adminLoginPage">
        <div className="adminLoginCard">
          <div className="adminLoginTitle">Admin přihlášení</div>
          <div className="adminLoginText">
            Zadej administrátorský klíč pro správu obsahu webu.
          </div>

          <form className="adminLoginForm" onSubmit={submitLogin}>
            <input
              className="adminInlineInput"
              type="password"
              value={loginKey}
              onChange={(event) => setLoginKey(event.target.value)}
              placeholder="Administrátorský klíč"
              autoComplete="current-password"
            />
            <button className="adminSaveBtn adminLoginBtn" type="submit" disabled={loginBusy || !loginKey.trim()}>
              {loginBusy ? "Přihlašuji…" : "Přihlásit"}
            </button>
          </form>

          {loginMsg ? <div className="adminLoginMsg">{loginMsg}</div> : null}
        </div>
      </div>
    );
  }

  if (!data) {
    return <div style={{ padding: 20 }}>Načítám…</div>;
  }

  return (
    <>
      <div className="adminBar">
        <div className="wrap adminBarRow">
          <div className="adminBarTop">
            <div className="adminBarTitle">Admin mód.</div>
            <button className="adminLogoutBtn" onClick={logout} type="button">
              Odhlásit
            </button>
          </div>

          <div className="adminBarBottom">
            <div className="adminBarActions">
              <button className="adminSaveBtn" onClick={saveSiteJson} disabled={siteBusy} type="button">
                Uložit
              </button>
              <button
                className="adminSaveBtn"
                onClick={restoreLatestBackup}
                disabled={siteBusy || !latestBackup}
                type="button"
              >
                Obnovit ze zálohy
              </button>
            </div>

            {siteMsg ? (
              <div className="adminBarMeta">
                {siteMsg}
                {latestBackup?.name ? ` • Poslední záloha: ${latestBackup.name}` : ""}
              </div>
            ) : latestBackup?.name ? (
              <div className="adminBarMeta">Poslední záloha: {latestBackup.name}</div>
            ) : null}
          </div>
        </div>
      </div>

      <HomeLayout
        isAdmin
        alertText={data.alert.text}
        onChangeAlertText={(v) => setData((p) => ({ ...p, alert: { ...p.alert, text: v } }))}
        contact={data.contact}
        onChangeContact={(patch) => setData((p) => ({ ...p, contact: { ...p.contact, ...patch } }))}
        aboutText={data.aboutText}
        onChangeAboutText={(v) => setData((p) => ({ ...p, aboutText: v }))}
        dailyMenu={data.dailyMenu}
        onChangeDailyMenu={(patch) => setData((p) => ({ ...p, dailyMenu: { ...p.dailyMenu, ...patch } }))}
        stableMenu={stableMenu}
        stableMenuAdmin={{
          enabled: menuEnabled,
          setEnabled: setStableEnabled,
          current: menuCurrent,
          setCurrent: setCurrentPdf,
          files: menuFiles,
          uploadDate,
          setUploadDate,
          uploadBusy,
          uploadMsg,
          uploadPdf,
          refresh: loadMenu,
          deleteCurrent: deletePdf,
        }}
        gallery={{ enabled: galleryEnabled, images: galleryImages }}
        galleryAdmin={{
          enabled: galleryEnabled,
          setEnabled: setGalleryEnabledApi,
          images: galleryImages,
          uploadBusy: galleryBusy,
          uploadMsg: galleryMsg,
          uploadImage,
          deleteImage,
          refresh: loadGallery,
        }}
      />
    </>
  );
}
